


























































































































































































































































































































































































/**
 * <Table /> Component
 *
 * It's essentially a wrapper around the v-data-table from Vuetify
 * which can work in two different modes - uncontrolled and controlled.
 *
 * "Controlled mode" means the parent component controls the majority of the behaviour of this table.
 * In this scenario, parent component handles the pagination, sorting, filtering - for example, when these
 * parameters are handled by a REST API.
 * Controlled mode requires a few extra pieces of information. Most importantly headers and rows information
 * can be objects, so that the displayed value is backed by an id of that piece of information, which is
 * extremely important in API-driven tables.
 * Controlled mode also provides with a @update:query-data event, which signifies that there was an update to
 * one of the parameters which are used to load the data for the table. Parent component can listen to this event,
 * catch the query data and load the updated content from the REST API, for example.
 *
 * "Uncontrolled mode" means that most of the table-related operations are handled by the table itself.
 * An uncontrolled table will handle pagination, filtering and sorting in the front-end environment in
 * real time. It's a much better option for tables which don't need to be backed by an API at every step.
 * Uncontrolled tables don't emit as much data and don't allow objects as headers/rows fields, because
 * they are backed by relatively simple and shallow querying algorithms.
 *
 * "Advanced Filters" - being a list of options what users can select from to filter their table,
 * is available in both modes, with the exception that Uncontrolled mode only allows strings and numbers,
 * while controlled mode only accepts objects. Such object should have name (name that will be displayed),
 * id (reference for the query object), queryKey (name of the column in the API call) and
 * querySortKey (key which is used by the API to understand which parameter exactly we're sorting with)
 */

  import {
    Component,
    Prop,
    Vue,
    Watch,
  } from 'vue-property-decorator';
  import { debounce } from '../classes/utils';
  import { IDataTableHeader, IDataTableSelectableFilterValue } from '../types';
  import {Getter, Mutation} from 'vuex-class';
  import PasteBox from '@/components/PasteBox.vue';

  @Component({
    components: {PasteBox},
  })
  export default class DataTable extends Vue {

    /**
     * Returns a list of table-related slots passed from the parent component
     * @returns {Array<any>}
     */
    private get tableSlots(): any[] {
      return Object.keys(this.$scopedSlots).filter((key) => 'actions' !== key);
    }

    /**
     * Returns currently selected number of rows per page
     * That comes either from props or the data object, depending if
     * the component is being controlled from the outside or not
     * @returns {number}
     */
    private get perPage(): number {
      return this.itemsPerPage ? this.itemsPerPage : this.internalItemsPerPage;
    }

    /**
     * Returns currently selected page
     * That comes either from props or the data object, depending if
     * the component is being controlled from the outside or not
     * @returns {number}
     */
    private get currentPage(): number {
      return this.page ? this.page : this.internalPage;
    }

    /**
     * Returns currently selected rows
     * It might come from both the props or the data object
     * depending if the component is running in controlled mode or not
     */
    private get selectedItems(): any[] {
      return this.value ? this.value : this.selected;
    }

    /**
     * Returns an object containing easy-to-read information about the currently selected filters
     * Each key in the object is the name of the column that's being filtered, with corresponding
     * values in the array
     * @returns {Object}
     */
    private get selectedFilters(): {[key: string]: any[]} {
      const checkedFilters = Object.keys(this.checked);
      const result: {[key: string]: any[]} = {};

      checkedFilters.forEach((filterPath) => {
        if (!this.checked[filterPath]) {
          return;
        }

        const [header, filterValue] = filterPath.split('__');
        result[header] = result[header] || [];
        result[header].push(filterValue);
      });

      return result;
    }

    /**
     * Returns rows filtered with the advanced filters,
     * or all rows when table is controlled (filtering is handled server-side)
     * @returns {Array<any>}
     */
    private get filteredRows(): any[] {
      if (this.controlled) {
        return this.items;
      }

      if (Object.keys(this.selectedFilters).length === 0) {
        return this.items;
      }

      let result = this.items;

      Object.keys(this.selectedFilters).forEach((headerName: string) => {
        result = result.filter((item: {[key: string]: any}) => this.selectedFilters[headerName].includes(item[headerName]));
      });

      return result;
    }


    /**
     * Returns an object containing information in a format more natural for the API request
     * @returns {Object}
     */
    private get queryData(): { sort?: string, desc?: boolean, filters?: {[key: string]: any}, timeFilters?: {[key: string]: Date[]}, take?: number, skip?: number } {
      let sortBy: string = this.sortBy;

      if (this.controlled && sortBy) {
        const header = this.headers.find((h: { querySortKey?: string, value: string }) => h.querySortKey && h.value === sortBy);
        if (header && header.querySortKey) {
          sortBy = header.querySortKey;
        }
      }

      // map the filters which only allow single value selected
      const selectedFilters = this.selectedFilters;
      Object.keys(selectedFilters).forEach((filter: string) => {
        const correspondingHeader = this.headers.find((header: IDataTableHeader) => {
          return header.queryKey === filter;
        });

        if (!correspondingHeader) {
          return;
        }

        if (correspondingHeader.singleSelectAdvancedFilters && selectedFilters[filter] instanceof Array && selectedFilters[filter].length > 0) {
          selectedFilters[filter] = selectedFilters[filter][0];
        }
      });

      return {
        sort: sortBy,
        desc: this.sortDesc,
        timeFilters: this.selectedTimeFilters,
        filters: this.selectedFilters,
        take: this.perPage,
        skip: (this.currentPage - 1) * this.perPage,
      };
    }

    /**
     * Returns an array of table columns that do not use custom rendering via passed slots
     * @returns {Array<object>}
     */
    private get defaultRenderedColumns(): object[] {
      return this.headers.filter((header: { value: string }) => !this.tableSlots.includes(`item.${header.value}`));
    }

    /**
     * Returns actual table height - if dynamic what has been calculated, otherwise user specified or default (undefined)
     * @returns {number | string | undefined}
     */
    private get actualTableHeight(): number | string | undefined {
      return this.dynamicHeight ? this.dataTableHeight : this.tableHeight;
    }

    @Getter private storedItemsPerPage!: (key: string) => number;
    @Mutation private setItemsPerPage: any;
    @Prop() private headers!: IDataTableHeader[]; // array of headers
    @Prop() private items!: any[];
    @Prop() private value!: any[];
    @Prop({default: true}) private dense!: boolean; // dense style by default
    @Prop({default: true}) private showFirstLastPage!: boolean; // show first/last page selection
    @Prop({default: false}) private singleSelect!: boolean; // if true, allows to select one row at a time, mutliple select is allowed when
    @Prop({default: false}) private controlled!: boolean; // true if table is in controlled mode, false otherwise
    @Prop({default: false}) private disablePagination!: boolean; // disables pagination for the entire table
    @Prop({default: false}) private showActions!: boolean; // show actions on top?
    @Prop({default: false}) private showCheckboxes!: boolean; // should it show checkboxes next to each row to select it?
    @Prop({default: false}) private fixedHeader!: boolean; // if true, the header will be fixed (see v-data-table docs for reference)
    @Prop({default: false}) private hideHeader!: boolean; // if true, the header will be hidden
    @Prop({default: false}) private hideFooter!: boolean; // if true, the footer will be hidden
    @Prop({default: false}) private itemsClickable!: boolean; // should the rows have a pointer cursor?
    @Prop({default: false}) private showExpand!: boolean; // should the rows expand
    @Prop({default: false}) private singleExpand!: boolean; // should one row expand at a time
    @Prop({default: false}) private loading!: boolean; // changes the loading state of the table
    @Prop({default: false}) private persistPaginationOptions!: boolean;

    @Prop() private totalCount?: number; // used only in controlled mode; how many entries there are on the server in total?
    @Prop({default: 20}) private itemsPerPage!: number; // how many rows to display at once?
    @Prop({default: 1}) private page!: number;
    @Prop({default: undefined}) private tableHeight!: number | string | undefined;
    @Prop({default: ''}) private search!: string;
    /* tslint:disable:no-empty */
    @Prop({default: () => []}) private paginationOptions!: number[]; // "per page" selection options
    @Prop({default: () => {}}) private customSort!: any; // custom client side sort
    @Prop({default: () => {}}) private customFilter!: any; // custom client side filter
    @Prop({default: () => {}}) private externalFilters!: any; // externally pre-selected filters
    @Prop({default: () => {}}) private options: any;
    @Prop({default: () => {}}) private itemClass: any;
    @Prop({default: () => 'id'}) private itemKey!: string;
    @Prop({default: () => null}) private groupBy: any;
    @Prop({default: false}) private dynamicHeight!: boolean;
    /* tslint:enable:no-empty */

    private selected: any[] = []; // which rows have been selected with a checkbox?
    private expanded: any[] = []; // which rows have been expanded
    private checked: any = {}; // which filter options are checked? This gets computed to selectedFilters
    private sortBy: string = ''; // which field to sort by? Ref: header.value
    private sortDesc: boolean = false; // true if sorting mode is descending, false otherwise
    private internalPage: number = 1; // current page
    private internalItemsPerPage: number = this.itemsPerPage; // current "rows per page" count, local to the component
    private filterMenuShownDict: { [key: string]: boolean } = {}; // Dictionary of filter menus that are shown.
    private dataTableHeight: number = 100; // Dynamic height of the table
    private storeKey: string = '';
    private timeFilters: { [key: string]: Date[] } = {};
    private selectedTimeFilters: { [key: string]: Date[] } = {};
    private dirtyFilters: boolean = false;
    private filteringList: { [key: string]: boolean } = {};
    private filteredValues: any = {};
    private showFilterList: { [key: string]: boolean } = {};
    private previousQueryData: any = {};

    // We need to debounce emitting this event as properties change independently closely together
    private debouncedChangeQueryDataRaiseEvent = debounce((vue: Vue, value: any) => { vue.$emit('update:query-data', value); });

    private skipQueryDataUpdate: boolean = false;

    private beforeMount() {
      if (this.persistPaginationOptions) {
        this.storeKey = this.$route.name!;
        const storedItemsPerPage = this.storedItemsPerPage(this.storeKey);
        if (storedItemsPerPage !== -1 && this.itemsPerPage !== storedItemsPerPage) {
          this.skipQueryDataUpdate = true;
          this.$emit('update:items-per-page', storedItemsPerPage);
        }
      }
    }

    private mounted() {
      for (const header of this.headers) {
        if (header.queryKey) {
          Vue.set(this.showFilterList, header.queryKey, false);
          Vue.set(this.filteringList, header.queryKey, false);
        }
      }
    }

    private setShowFilterList(header: string) {
      Vue.set(this.showFilterList, header, !this.showFilterList[header]);
      Vue.set(this.filteringList, header, !this.filteringList[header]);
    }

    /**
     * Dynamic resize of the table
     * @returns {void}
     */
    private onContainerResize() {
      // YUCKY HACK to ensure we get the right sizes on this event.
      window.setTimeout(() => {
        const container = (this.$refs.tableContainer as any);
        if (container) {
          Vue.set(this, 'dataTableHeight', container.$el.clientHeight - (this.showActions ? (this.$refs.actionsMenu as any).clientHeight : 0));
          this.$forceUpdate();
        }
      }, 250);
    }

    @Watch('filterMenuShownDict', {deep: true})
    private onFilterMenuShown(curr: any, prev: any) {
      for (const header in this.filterMenuShownDict) {
        if (this.filterMenuShownDict[header]) {
          const h = this.headers.find((hd) => hd.value === header);
          if (!h) {
            return;
          }
          if (!(this.filteredValues[h.queryKey!] && this.filteredValues[h.queryKey!].length) || !curr[header]) {
            return;
          }
          this.setShowFilterList(h.queryKey!);
        }
      }
    }

    @Watch('externalFilters',  {immediate: true})
    private onExternalFilterChange() {
      if (this.externalFilters && Object.keys(this.externalFilters).length) {
        Object.entries(this.externalFilters).forEach(([header, filterValue]) => {
          String(filterValue).split(',').forEach((val) => {
            if (val.length) {
              Vue.set(this.checked, `${header}__${val}`, true);
            }
          });
        });
      }
    }

    @Watch('selected')
    private onSelectedChange(value: any[]): void {
      // array of selected rows is the v-model of this table
      this.$emit('input', value);
    }

    @Watch('expanded')
    private onExpandedChange(value: any[]): void {
      // array of expanded rows is the v-model of this table
      this.$emit('expand', value);
    }

    @Watch('internalItemsPerPage')
    private onInternalItemsPerPageChange() {
      // when the number of "per page" items has been changed,
      // emit the event to inform the parent component about it
      this.$emit('update:items-per-page', this.internalItemsPerPage);
    }

    @Watch('internalPage')
    private onInternalPageChange() {
      // when the number of "per page" items has been changed,
      // emit the event to inform the parent component about it
      this.$emit('update:page', this.internalPage);
    }

    @Watch('queryData', { deep: true })
    private onChangeQueryData(value: any): void {
      if (this.skipQueryDataUpdate) {
        this.skipQueryDataUpdate = false;
        return;
      }

      if (JSON.stringify(value) !== JSON.stringify(this.previousQueryData)) {
        this.previousQueryData = { ...value };
        this.debouncedChangeQueryDataRaiseEvent(this, value);
      }
    }


    @Watch('itemsPerPage')
    private onItemsPerPageUpdate() {
      if (this.persistPaginationOptions) {
        this.setItemsPerPage({itemsPerPage: this.itemsPerPage, key: this.storeKey});
      }
    }

    /**
     * Handles the sorting button click on the "advanced filter" popup
     * @param {string} field - name of the field (column)
     * @param {boolean} isDesc - true if the ordering is descending, false otherwise
     */
    private handleCustomSortTable(field: string, isDesc: boolean): void {
      // if it's a click or currently activated sort button, disable it
      if (this.sortBy === field && this.sortDesc === isDesc) {
        this.sortBy = '';
        this.sortDesc = false;
        return;
      }
      this.sortBy = field || '';
      this.sortDesc = isDesc || false;
    }


    private setFilterValues(header: IDataTableHeader, e: any) {
      if (header.queryKey) {
        Vue.set(this.filteredValues, header.queryKey, e);
      }
    }

    private onAdvancedFilterChange(value: boolean, key: string) {
      if (!value) {
        return;
      }

      const prefix = key.split('__')[0];
      Object.keys(this.checked).filter((checkedKey: string) => {
        if (checkedKey === key || !this.checked[checkedKey]) {
          return false;
        }

        return checkedKey.startsWith(`${prefix}__`);
      }).forEach((checkedKey: string) => {
        this.checked[checkedKey] = false;
      });
    }

    /**
     * Handles clear filters button press
     * @param {string} key
     */
    private clearFilter(key?: string) {
      if (!key) { return; }
      const keys = Object.keys(this.checked).filter((objKey: string) => objKey.startsWith(`${key}__`));
      keys.forEach((objKey) => {
        this.checked[objKey] = false;
      });

      this.filteredValues[key] = [];
    }
    /**
     * Handles clear time filters button press
     * @param {string} key
     */
    private clearTimeFilter() {
      this.previousQueryData = {
        ...this.previousQueryData,
        timeFilters: { ...this.selectedTimeFilters },
      };

      Object.keys(this.selectedTimeFilters).forEach((timeFilterKey: string) => {
        this.timeFilters[timeFilterKey] = [];
        this.selectedTimeFilters[timeFilterKey] = [];
        delete this.timeFilters[timeFilterKey];
        delete this.selectedTimeFilters[timeFilterKey];

        this.filterMenuShownDict[timeFilterKey] = false;
      });
    }


    private applyFilter(header: any) {
      if (!Object.keys(this.filteredValues).length) {
        return;
      }
      const keys = Object.keys(this.checked).filter((objKey: string) => objKey.startsWith(`${header.queryKey}__`));
      keys.forEach((objKey) => {
        this.checked[objKey] = false;
      });

      this.filteredValues[header.queryKey].forEach((filterValue: any) => {
        let searchVal: { id: number } | null = null;
        if (typeof filterValue === 'string') {
          searchVal = header.allowedFilterValues.find((v: { name: string }) => v.name.toLowerCase() === filterValue.toLowerCase());
        } else if (filterValue.value.length) {
          searchVal = header.allowedFilterValues.find((v: { name: string }) => v.name.toLowerCase() === filterValue.value.toLowerCase());
        }
        if (searchVal) {
          Vue.set(this.checked, `${header.queryKey}__${searchVal.id}`, true);
        }
      });
    }

    private applyTimeFilters() {
      const timeFilters = Object.keys(this.timeFilters);
      const result: { [key: string]: any[] } = {};

      timeFilters.forEach((header) => {
        result[header] = result[header] || [];
        const sorted = [this.timeFilters[header][0], this.timeFilters[header][1]].sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime(),
        );
        if (!(sorted[0] && sorted[1])) {
          result[header] = [];
        } else {
          result[header].push(sorted[0], sorted[1]);
        }
        this.filterMenuShownDict[header] = false;
      });

      this.selectedTimeFilters = { ...result };
    }
  }
